import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Loader2, PanelRightOpen } from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useFinalizeQuotation } from "@/api/expedition.api";
import { Accordion } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { NoteType } from "@/constants/enums/noteType.enum";
import {
  FinalizeExpeditionFormData,
  FinalizeExpeditionFormSchema,
} from "@/types/forms/bo/expeditionBo.form";
import { ExpeditionFormStepProps } from "@/components/backOffice/expeditionForm/expeditionForm.types";
import { ExpeditionHeader } from "@/components/backOffice/expeditionForm/expeditionHeader";
import { ExpeditionSummary } from "@/components/backOffice/expeditionForm/shared/finalize/expeditionSummary";
import {
  FileContent,
  FileContentWoApi,
} from "@/components/backOffice/expeditionForm/shared/finalize/fileContent";
import { ShippingLabel } from "@/components/backOffice/expeditionForm/shared/finalize/shippingLabel";
import { TrackingInfo } from "@/components/backOffice/expeditionForm/shared/finalize/trackingInfo";
import {
  SideContent,
  SideContentMobileModal,
} from "@/components/backOffice/expeditionForm/shared/sections/sidePanel/sideContent";
import { useGetCustomV1Bo } from "@/api/customs.api";
import { toast } from "react-toastify";

export const QuotationExpeditionFormStepTwo = ({
  expedition,
  company,
  apiEnum,
  formStep,
  setFormStep,
}: ExpeditionFormStepProps) => {
  const { t } = useTranslation("boExpeditionForm");
  const {
    mutateAsync: finalizeQuotation,
    isPending: isFinalizingQuotation,
    isSuccess: isFinalized,
  } = useFinalizeQuotation();
  const {
    data: custom,
    isLoading: isLoadingCustoms,
    isError: isErrorCustoms,
  } = useGetCustomV1Bo(expedition.quotation?.customId ?? "");

  const navigate = useNavigate();

  const [isSideContentOpen, setIsSideContentOpen] = useState(false);

  const methods = useForm<FinalizeExpeditionFormData>({
    resolver: zodResolver(FinalizeExpeditionFormSchema),
    defaultValues: {
      apiEnum,
      trackingUrl: expedition.tracking?.trackingUrl ?? "",
      trackingCode: expedition.tracking?.trackingCode ?? "",
      packagingInstructions:
        expedition.expeditionNotes?.find((note) => note.type === NoteType.PACKAGING_INSTRUCTION)
          ?.note ?? "",
      packagingPriority: expedition.expeditionOptions?.packagingPriority ?? false,
    },
  });

  const onSubmit = async (data: FinalizeExpeditionFormData) => {
    if (expedition.notification?.some((n) => n.readBy == null)) {
      toast.error(t("formStepTwo.unreadNotifications"), { position: "top-center" });
      return;
    }
    try {
      await finalizeQuotation({
        id: expedition.id,
        payload: data,
      });
      navigate(-1);
    } catch (error) {
      console.error(error);
    }
  };

  if (isLoadingCustoms) {
    return <Loader2 className="animate-spin" />;
  }

  if (isErrorCustoms || !custom) {
    return <div>{t("formStepTwo.customLoadError")}</div>;
  }

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(onSubmit)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
          }
        }}
        className="@container absolute inset-0 flex flex-col overflow-hidden"
      >
        <ExpeditionHeader expedition={expedition} formStep={formStep} setStep={setFormStep} />
        <div className="relative flex w-full flex-col justify-between gap-2 overflow-hidden @min-[896px]:flex-row">
          <Accordion
            type="multiple"
            className="@container flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4"
            defaultValue={["fileContent", "expeditionSummary", "trackingInfo", "shippingLabel"]}
          >
            {apiEnum === "NONE" ? (
              <FileContentWoApi expedition={expedition} custom={custom} />
            ) : (
              <FileContent expedition={expedition} custom={custom} />
            )}
            <ExpeditionSummary expedition={expedition} />
            {apiEnum === "NONE" && <TrackingInfo />}
            {apiEnum === "NONE" && !expedition.expeditionOptions?.handDelivery && (
              <ShippingLabel expedition={expedition} />
            )}
            <Button
              type="submit"
              className="w-fit self-start"
              disabled={isFinalizingQuotation || isFinalized}
            >
              {isFinalizingQuotation ? (
                <Loader2 className="animate-spin" />
              ) : (
                t("formStepTwo.submit")
              )}
            </Button>
          </Accordion>
          <div className="hidden min-h-0 w-[440px] flex-col gap-4 overflow-y-auto p-4 @min-[896px]:flex">
            <SideContent expedition={expedition} company={company} custom={custom} readOnly />
          </div>
          <Button
            type="button"
            variant="default"
            onClick={() => setIsSideContentOpen(true)}
            aria-label={t("formStepTwo.openDetails")}
            className="absolute right-4 bottom-4 z-30 flex h-12 w-12 items-center justify-center rounded-full shadow-lg @min-[896px]:hidden [&_svg]:h-6 [&_svg]:w-6"
          >
            <PanelRightOpen />
          </Button>
          <SideContentMobileModal
            open={isSideContentOpen}
            onClose={() => setIsSideContentOpen(false)}
            expedition={expedition}
            company={company}
            custom={custom}
            readOnly
          />
        </div>
      </form>
    </FormProvider>
  );
};
